import React from "react";
import CrudManager from "../../common/CrudManager";
import { Menu, ExternalLink, Hash, Link as LinkIcon } from "lucide-react";

const LOCATION_LABELS = {
  "navbar-main": { label: "Navbar Main", color: "bg-blue-100 text-blue-700" },
  "navbar-mobile": {
    label: "Navbar Mobile",
    color: "bg-indigo-100 text-indigo-700",
  },
  "footer-company": {
    label: "Footer Company",
    color: "bg-purple-100 text-purple-700",
  },
  "footer-admin": { label: "Footer Admin", color: "bg-pink-100 text-pink-700" },
  "footer-legal": { label: "Footer Legal", color: "bg-teal-100 text-teal-700" },
};

const LINK_TYPE_ICONS = {
  internal: LinkIcon,
  external: ExternalLink,
  hash: Hash,
  none: Menu,
};

const NavigationItemManager = ({ location, onClose }) => {
  const fields = [
    {
      name: "location",
      label: "Location",
      type: "select",
      options: [
        { value: "navbar-main", label: "Navbar - Main Menu" },
        { value: "navbar-mobile", label: "Navbar - Mobile Menu" },
        { value: "footer-company", label: "Footer - Company Column" },
        { value: "footer-admin", label: "Footer - Admin Column" },
        { value: "footer-legal", label: "Footer - Legal Links" },
      ],
      required: true,
    },
    {
      name: "labelEn",
      label: "Label (English)",
      type: "text",
      placeholder: "e.g., About Us",
      required: true,
    },
    {
      name: "labelJa",
      label: "Label (Japanese)",
      type: "text",
      placeholder: "例：会社概要",
      required: false,
    },
    {
      name: "linkType",
      label: "Link Type",
      type: "select",
      options: [
        { value: "internal", label: "Internal Page (/apply)" },
        { value: "hash", label: "Hash Link (/#about)" },
        { value: "external", label: "External URL (https://...)" },
        { value: "none", label: "No Link (Label Only)" },
      ],
      required: true,
    },
    {
      name: "linkUrl",
      label: "Link URL",
      type: "text",
      placeholder: "/apply or /#about or https://...",
      required: false,
    },
    {
      name: "iconName",
      label: "Icon Name (Optional)",
      type: "text",
      placeholder: "Lucide icon name (e.g., User, Mail)",
      required: false,
    },
    {
      name: "isHighlight",
      label: "Highlight Style",
      type: "select",
      options: [
        { value: "false", label: "Normal" },
        { value: "true", label: "Highlight (CTA style)" },
      ],
      required: false,
    },
    {
      name: "order",
      label: "Display Order",
      type: "text",
      placeholder: "0",
      required: false,
    },
    {
      name: "isPublished",
      label: "Published",
      type: "select",
      options: [
        { value: "true", label: "Published" },
        { value: "false", label: "Draft" },
      ],
      required: false,
    },
  ];

  const formatCreateData = (formData) => ({
    location: formData.location,
    labelEn: formData.labelEn.trim(),
    labelJa: formData.labelJa?.trim() || undefined,
    linkType: formData.linkType,
    linkUrl: formData.linkUrl?.trim() || "",
    iconName: formData.iconName?.trim() || undefined,
    isHighlight: formData.isHighlight === "true",
    order: formData.order ? parseInt(formData.order, 10) : 0,
    isPublished: formData.isPublished !== "false",
  });

  const formatUpdateData = (formData) => ({
    location: formData.location,
    labelEn: formData.labelEn.trim(),
    labelJa: formData.labelJa?.trim() || undefined,
    linkType: formData.linkType,
    linkUrl: formData.linkUrl?.trim() || "",
    iconName: formData.iconName?.trim() || undefined,
    isHighlight: formData.isHighlight === "true",
    order: formData.order ? parseInt(formData.order, 10) : 0,
    isPublished: formData.isPublished !== "false",
  });

  const renderItem = (item, onEdit, onDelete) => {
    const locationMeta = LOCATION_LABELS[item.location] || {
      label: item.location,
      color: "bg-slate-100 text-slate-600",
    };

    const LinkTypeIcon = LINK_TYPE_ICONS[item.linkType] || LinkIcon;

    return (
      <div className="flex items-start justify-between w-full group gap-4">
        <div className="flex items-start space-x-4 flex-1 min-w-0">
          <div className="w-12 h-12 bg-subidha-blue/5 rounded-2xl flex items-center justify-center border border-subidha-blue/10 group-hover:bg-subidha-blue transition-all duration-300 shrink-0">
            <LinkTypeIcon className="w-5 h-5 text-subidha-blue group-hover:text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h3 className="text-sm font-black text-slate-900">
                {item.labelEn}
              </h3>
              <span
                className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${locationMeta.color}`}
              >
                {locationMeta.label}
              </span>
              {item.isHighlight && (
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                  CTA
                </span>
              )}
            </div>
            {item.linkUrl && (
              <p className="text-xs text-subidha-blue font-mono mb-1">
                {item.linkUrl}
              </p>
            )}
            <div className="flex items-center gap-2 text-[10px] text-slate-400">
              <span>{item.linkType}</span>
              {item.iconName && (
                <>
                  <span>•</span>
                  <span className="font-mono">{item.iconName}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1 shrink-0">
          <span className="text-[10px] font-black text-slate-300 mr-2">
            #{item.order ?? 0}
          </span>
          {onEdit && (
            <button
              onClick={() => onEdit(item)}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              title="Edit"
            >
              <Menu className="w-4 h-4 text-slate-400" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(item)}
              className="p-2 hover:bg-red-50 rounded-xl transition-colors"
              title="Delete"
            >
              <span className="w-4 h-4 text-red-400">×</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <CrudManager
      title="Manage Navigation Items"
      apiEndpoint={
        location
          ? `/navigation-items?location=${location}`
          : "/navigation-items"
      }
      fields={fields}
      renderItem={renderItem}
      onClose={onClose}
      icon={Menu}
      emptyMessage="No navigation items yet. Add your first link!"
      formatCreateData={formatCreateData}
      formatUpdateData={formatUpdateData}
    />
  );
};

export default NavigationItemManager;
